import React from 'react';
import { FD130, FD132 } from '../../../services/pwd212Api';
import { dateToInput, inputToDate, fmt, PROCESSING_CATEGORY, PRICING_METHOD, MATERIAL_CATEGORY } from '../helpers';

interface Tab1Props {
  formData: Partial<FD130>;
  productDesc: Partial<FD132>;
  names: Record<string, string>;
  isEditing: boolean;
  isFieldDisabled: (key: string) => boolean;
  getField: (key: keyof FD130) => FD130[keyof FD130];
  setField: (key: keyof FD130, value: FD130[keyof FD130]) => void;
  getPDField: (key: keyof FD132) => FD132[keyof FD132];
  setPDField: (key: keyof FD132, value: FD132[keyof FD132]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
  getFieldError?: (field: string) => string | undefined;
}

const Tab1BasicInfo: React.FC<Tab1Props> = React.memo(({
  formData, names, isEditing, isFieldDisabled,
  getField, setField, getPDField, setPDField,
  resolveSingleName, getFieldError,
}) => {
  const E = (field: string) => {
    const err = getFieldError?.(field);
    return err ? <span style={{ color: '#ff4d4f', fontSize: 11, marginLeft: 6 }}>{err}</span> : null;
  };

  return (
    <>
      {/* Section 1: 基本資料 */}
      <div className="form-section odd">
        <span className="section-tag">基本資料</span>
        <div className="form-grid">
          <label>託工單號</label>
          <input type="text" readOnly style={{ maxWidth: 140 }} value={getField('fd130_01') as string} />

          <label>胚布預估單號{E('fd130_78')}</label>
          <input type="text" style={{ maxWidth: 140 }}
            disabled={isFieldDisabled('fd130_78')}
            value={getField('fd130_78') as string}
            onChange={e => setField('fd130_78', e.target.value)}
          />

          <label>託工日期{E('fd130_11')}</label>
          <input type="date" style={{ maxWidth: 160 }}
            disabled={isFieldDisabled('fd130_11')}
            value={dateToInput(getField('fd130_11') as string)}
            onChange={e => setField('fd130_11', inputToDate(e.target.value) as string)}
          />

          <label>預定交期{E('fd130_13')}</label>
          <input type="date" style={{ maxWidth: 160 }}
            disabled={isFieldDisabled('fd130_13')}
            value={dateToInput(getField('fd130_13') as string)}
            onChange={e => setField('fd130_13', inputToDate(e.target.value) as string)}
          />

          <label>加工廠{E('fd130_12')}</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 100 }} maxLength={8}
              disabled={isFieldDisabled('fd130_12')}
              value={getField('fd130_12') as string}
              onChange={e => setField('fd130_12', e.target.value)}
              onBlur={() => resolveSingleName('processor', 'company', getField('fd130_12') as string)}
            />
            <span className="lookup-name">{names.processor || '--'}</span>
          </div>

          <label>入單人員</label>
          <div className="lookup-field">
            <input type="text" readOnly style={{ width: 100 }} value={getField('fd130_33') as string} />
            <span className="lookup-name">{names.entryPerson || '--'}</span>
          </div>

          <label>工繳代號{E('fd130_15')}</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 100 }} maxLength={8}
              disabled={isFieldDisabled('fd130_15')}
              value={getField('fd130_15') as string}
              onChange={e => setField('fd130_15', e.target.value)}
              onBlur={() => resolveSingleName('processingFee', 'processingFeeCode', getField('fd130_15') as string)}
            />
            <span className="lookup-name">{names.processingFee || '--'}</span>
          </div>

          <label>預估縮率%</label>
          <input type="number" style={{ maxWidth: 100 }}
            disabled={isFieldDisabled('fd130_14')}
            value={getField('fd130_14') as number || ''}
            onChange={e => setField('fd130_14', parseFloat(e.target.value) || 0)}
          />

          <label>採購人員{E('fd132_15')}</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 100 }} maxLength={8}
              disabled={!isEditing}
              value={getPDField('fd132_15') as string}
              onChange={e => setPDField('fd132_15', e.target.value)}
              onBlur={() => resolveSingleName('purchaser', 'employee', getPDField('fd132_15') as string)}
            />
            <span className="lookup-name">{names.purchaser || '--'}</span>
          </div>

          <label>樣布需要量</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 100 }} maxLength={8}
              disabled={isFieldDisabled('fd130_35')}
              value={getField('fd130_35') as string}
              onChange={e => setField('fd130_35', e.target.value)}
              onBlur={() => resolveSingleName('sampleReq', 'sampleRequirement', getField('fd130_35') as string)}
            />
            <span className="lookup-name">{names.sampleReq || '--'}</span>
          </div>

          <label>入布廠商</label>
          <input type="text" style={{ maxWidth: 200 }}
            disabled={isFieldDisabled('fd130_36')}
            value={getField('fd130_36') as string}
            onChange={e => setField('fd130_36', e.target.value)}
          />
        </div>
      </div>

      {/* Section 2: 計價與幣別 */}
      <div className="form-section even">
        <span className="section-tag">計價與幣別</span>
        <div className="form-grid">
          <label>計價方式{E('fd130_40')}</label>
          <div className="radio-group">
            {Object.entries(PRICING_METHOD).map(([val, label]) => (
              <label key={val}>
                <input type="radio" name="fd130_40"
                  disabled={isFieldDisabled('fd130_40')}
                  checked={(getField('fd130_40') as number) === Number(val)}
                  onChange={() => setField('fd130_40', Number(val))}
                />
                {label}
              </label>
            ))}
          </div>

          <label>幣別{E('fd130_45')}</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 80 }} maxLength={6}
              disabled={isFieldDisabled('fd130_45')}
              value={getField('fd130_45') as string}
              onChange={e => setField('fd130_45', e.target.value)}
              onBlur={() => resolveSingleName('currency', 'currency', getField('fd130_45') as string)}
            />
            <span className="lookup-name">{names.currency || '--'}</span>
          </div>

          <label>匯率</label>
          <input type="number" readOnly style={{ maxWidth: 100 }} step="0.0001"
            value={getField('fd130_46') as number || ''}
          />

          <label>交易條件{E('fd130_47')}</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 80 }} maxLength={4}
              disabled={isFieldDisabled('fd130_47')}
              value={getField('fd130_47') as string}
              onChange={e => setField('fd130_47', e.target.value)}
              onBlur={() => resolveSingleName('tradeTerms', 'tradeTerms', getField('fd130_47') as string)}
            />
            <span className="lookup-name">{names.tradeTerms || '--'}</span>
          </div>

          <label>付款條件{E('fd130_471')}</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 80 }} maxLength={4}
              disabled={isFieldDisabled('fd130_471')}
              value={getField('fd130_471') as string}
              onChange={e => setField('fd130_471', e.target.value)}
              onBlur={() => resolveSingleName('paymentTerms', 'paymentTerms', getField('fd130_471') as string)}
            />
            <span className="lookup-name">{names.paymentTerms || '--'}</span>
          </div>

          <label>包裝代號{E('fd130_16a')}</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 80 }} maxLength={4}
              disabled={isFieldDisabled('fd130_16a')}
              value={getField('fd130_16a') as string}
              onChange={e => setField('fd130_16a', e.target.value)}
              onBlur={() => resolveSingleName('packing', 'packing', getField('fd130_16a') as string)}
            />
            <span className="lookup-name">{names.packing || '--'}</span>
          </div>

          <label>工繳合計金額</label>
          <input type="text" readOnly style={{ maxWidth: 140, textAlign: 'right' }}
            value={fmt(formData.fd130_34)}
          />
        </div>
      </div>

      {/* Section 3: 加工與分類 */}
      <div className="form-section odd">
        <span className="section-tag">加工與分類</span>
        <div className="form-grid">
          <label>加工類別</label>
          <div className="radio-group">
            {Object.entries(PROCESSING_CATEGORY).map(([val, label]) => (
              <label key={val}>
                <input type="radio" name="fd130_48"
                  disabled={isFieldDisabled('fd130_48')}
                  checked={(getField('fd130_48') as number) === Number(val)}
                  onChange={() => setField('fd130_48', Number(val))}
                />
                {label}
              </label>
            ))}
          </div>

          <label>原料/半成品/成品{E('fd130_31')}</label>
          <div className="radio-group">
            {Object.entries(MATERIAL_CATEGORY).map(([val, label]) => (
              <label key={val}>
                <input type="radio" name="fd130_31"
                  disabled={isFieldDisabled('fd130_31')}
                  checked={(getField('fd130_31') as number) === Number(val)}
                  onChange={() => setField('fd130_31', Number(val))}
                />
                {label}
              </label>
            ))}
          </div>

          <label>單位{E('fd130_19')}</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 80 }} maxLength={4}
              disabled={isFieldDisabled('fd130_19')}
              value={getField('fd130_19') as string}
              onChange={e => setField('fd130_19', e.target.value)}
              onBlur={() => resolveSingleName('unit', 'unit', getField('fd130_19') as string)}
            />
            <span className="lookup-name">{names.unit || '--'}</span>
          </div>

          <label>Q.C 人員</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 100 }} maxLength={8}
              disabled={isFieldDisabled('fd130_27')}
              value={getField('fd130_27') as string}
              onChange={e => setField('fd130_27', e.target.value)}
              onBlur={() => resolveSingleName('qcPerson', 'employee', getField('fd130_27') as string)}
            />
            <span className="lookup-name">{names.qcPerson || '--'}</span>
          </div>

          <label>
            <span>開立三聯式發票</span>
          </label>
          <div className="checkbox-field">
            <input type="checkbox"
              disabled={isFieldDisabled('fd130_52')}
              checked={!!getField('fd130_52')}
              onChange={e => setField('fd130_52', e.target.checked)}
            />
          </div>
        </div>
      </div>

      {/* Section 4: 加工項目 (fd132_12, on Page 1 per spec) */}
      <div className="form-section even">
        <span className="section-tag">加工項目</span>
        <div className="textarea-group">
          <label>加工項目說明</label>
          <textarea rows={3}
            disabled={!isEditing}
            value={getPDField('fd132_12') as string}
            onChange={e => setPDField('fd132_12', e.target.value)}
          />
        </div>
      </div>
    </>
  );
});

Tab1BasicInfo.displayName = 'Tab1BasicInfo';
export default Tab1BasicInfo;
